import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import api from '@/utils/api';
import {
  BucketType,
  DispositionTree,
  DispositionOption,
  UpdateDispositionPayload,
  UpdateDispositionResponse,
  DispositionHistoryRecord
} from '@/types/disposition';

interface DispositionContextType {
  // Cached trees by bucket type
  trees: Record<BucketType, DispositionTree | null>;

  // Loading state
  loading: boolean;
  error: Error | null;

  // Methods
  getTree: (bucketType: BucketType) => Promise<DispositionTree>;
  getL1Options: (bucketType: BucketType) => DispositionOption[];
  getL2Options: (bucketType: BucketType, l1OptionId: number) => DispositionOption[];
  getL3Options: (bucketType: BucketType, l2OptionId: number) => DispositionOption[];
  getOptionById: (bucketType: BucketType, optionId: number) => DispositionOption | undefined;
  updateDisposition: (leadUuid: string, payload: UpdateDispositionPayload) => Promise<UpdateDispositionResponse>;
  getHistory: (leadUuid: string, limit?: number) => Promise<DispositionHistoryRecord[]>;
  clearCache: () => void;
}

const DispositionContext = createContext<DispositionContextType | undefined>(undefined);

export const DispositionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [trees, setTrees] = useState<Record<BucketType, DispositionTree | null>>({
    abandoned_cart: null,
    reorder: null,
    rto: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch and cache a disposition tree
  const getTree = useCallback(async (bucketType: BucketType): Promise<DispositionTree> => {
    // Return cached tree if available
    if (trees[bucketType]) {
      return trees[bucketType]!;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`📡 DispositionContext: Fetching tree for ${bucketType}...`);
      const response = await api.req<DispositionTree>({
        url: `/dispositions/tree/${bucketType}`,
        method: api.Method.GET,
      });

      if ((response.success || response.status === 'success') && response.data) {
        const tree = response.data as DispositionTree;
        setTrees(prev => ({ ...prev, [bucketType]: tree }));
        console.log(`✅ DispositionContext: Tree loaded for ${bucketType}`);
        return tree;
      } else {
        throw new Error('Failed to fetch disposition tree');
      }
    } catch (err) {
      console.error(`❌ DispositionContext: Failed to fetch tree for ${bucketType}:`, err);
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [trees]);

  // Get L1 options from cached tree
  const getL1Options = useCallback((bucketType: BucketType): DispositionOption[] => {
    const tree = trees[bucketType];
    if (!tree) return [];
    return tree.l1Options;
  }, [trees]);

  // Get L2 options for a given L1 option_id
  const getL2Options = useCallback((bucketType: BucketType, l1OptionId: number): DispositionOption[] => {
    const tree = trees[bucketType];
    if (!tree || !l1OptionId) return [];
    return tree.l2ByL1[l1OptionId] || [];
  }, [trees]);

  // Get L3 options for a given L2 option_id
  const getL3Options = useCallback((bucketType: BucketType, l2OptionId: number): DispositionOption[] => {
    const tree = trees[bucketType];
    if (!tree || !l2OptionId) return [];
    return tree.l3ByL2[l2OptionId] || [];
  }, [trees]);

  // Get a specific option by ID from the tree
  const getOptionById = useCallback((bucketType: BucketType, optionId: number): DispositionOption | undefined => {
    const tree = trees[bucketType];
    if (!tree || !optionId) return undefined;

    // Check L1 options
    const l1Option = tree.l1Options.find(o => o.option_id === optionId);
    if (l1Option) return l1Option;

    // Check L2 options
    for (const l2Options of Object.values(tree.l2ByL1)) {
      const l2Option = l2Options.find(o => o.option_id === optionId);
      if (l2Option) return l2Option;
    }

    // Check L3 options
    for (const l3Options of Object.values(tree.l3ByL2)) {
      const l3Option = l3Options.find(o => o.option_id === optionId);
      if (l3Option) return l3Option;
    }

    return undefined;
  }, [trees]);

  // Update a lead's disposition
  const updateDisposition = useCallback(async (
    leadUuid: string,
    payload: UpdateDispositionPayload
  ): Promise<UpdateDispositionResponse> => {
    setLoading(true);
    setError(null);

    try {
      console.log(`📡 DispositionContext: Updating disposition for ${leadUuid}...`);
      const response = await api.req<UpdateDispositionResponse>({
        url: `/dispositions/update/${leadUuid}`,
        method: api.Method.POST,
        body: payload,
      });

      if ((response.success || response.status === 'success') && response.data) {
        console.log(`✅ DispositionContext: Disposition updated for ${leadUuid}`);
        return response.data as UpdateDispositionResponse;
      } else {
        throw new Error('Failed to update disposition');
      }
    } catch (err) {
      console.error(`❌ DispositionContext: Failed to update disposition for ${leadUuid}:`, err);
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get disposition history for a lead
  const getHistory = useCallback(async (
    leadUuid: string,
    limit: number = 50
  ): Promise<DispositionHistoryRecord[]> => {
    try {
      console.log(`📡 DispositionContext: Fetching history for ${leadUuid}...`);
      const response = await api.req<DispositionHistoryRecord[]>({
        url: `/dispositions/history/${leadUuid}?limit=${limit}`,
        method: api.Method.GET,
      });

      if (response.status === 'success' && response.data) {
        return response.data as DispositionHistoryRecord[];
      }
      return [];
    } catch (err) {
      console.error(`❌ DispositionContext: Failed to fetch history for ${leadUuid}:`, err);
      return [];
    }
  }, []);

  // Clear the cache
  const clearCache = useCallback(() => {
    setTrees({
      abandoned_cart: null,
      reorder: null,
      rto: null,
    });
  }, []);

  return (
    <DispositionContext.Provider
      value={{
        trees,
        loading,
        error,
        getTree,
        getL1Options,
        getL2Options,
        getL3Options,
        getOptionById,
        updateDisposition,
        getHistory,
        clearCache
      }}
    >
      {children}
    </DispositionContext.Provider>
  );
};

// Custom hook to use the disposition context
export const useDisposition = () => {
  const context = useContext(DispositionContext);
  if (context === undefined) {
    throw new Error('useDisposition must be used within a DispositionProvider');
  }
  return context;
};
